import { useState } from 'react';
import isFunction from 'lodash/isFunction';
import styled from 'styled-components';
import { SIZES, SPACES } from 'common/constants/sizes';
import { getColor } from 'common/constants/_colors';
import { ClickAwayListener, Menu, MenuItem } from '@material-ui/core';
import { Flex } from './base/layout-components';
import Icon from './base/Icon';

export const SingleTeamLabelWrapper = styled.div`
  width: 22rem;
`;

const StyledItemPicker = styled.div`
  box-sizing: content-box;
  display: flex;
  align-items: center;
  margin-right: ${SPACES.small};
  padding: 0.8rem ${SPACES.small};
  border-radius: 4px;
  border: 1px solid ${getColor('botticelli')};
  height: 3rem;
  ${({ disabled }) => (disabled ? 'cursor: default' : 'cursor: pointer')};
  ${({ minWidth }) =>
    minWidth ? `min-width: ${minWidth};` : 'width: 16.5rem;'};

  ${({ disabled }) =>
    !disabled &&
    `:hover {
    border: 1px solid ${getColor('dodgerBlue')} !important;
    background-color: ${getColor('zircon')};
  }`}
`;

const StyledMenu = styled(Menu)`
  max-height: 36rem;
  transform: translateY(5rem);
  .MuiPaper-root.MuiMenu-paper.MuiPopover-paper {
    .MuiMenu-list {
      padding: 0;
    }
    ${({ pageyoffset }) =>
      pageyoffset ? `top: calc(16px - ${pageyoffset}) !important;` : ''};
    ${({ overlayItem }) => (overlayItem ? `overflow-y: hidden` : '')};
  }
`;

const getItemStyle = isSelected => {
  const regularStyle = {
    fontSize: SIZES.medium,
    minWidth: '22.5rem',
    margin: '0.5rem'
  };
  const selectedStyle = { backgroundColor: getColor('catskillWhite') };
  return {
    ...regularStyle,
    ...(isSelected ? selectedStyle : {})
  };
};

const ItemPicker = ({
  onItemChange,
  items,
  isSelectedFunc,
  itemMapper,
  children,
  dataAttrName,
  disabled,
  isOnTopBar = true,
  getOverlayItem = null,
  minWidth = null,
  menuStyle = {}
}) => {
  const [anchor, setAnchor] = useState(null);

  const handleItemChange = iteration => {
    onItemChange(iteration);
    setAnchor(null);
  };
  const OverlayItem =
    getOverlayItem &&
    isFunction(getOverlayItem) &&
    getOverlayItem({ setAnchor });

  return (
    <ClickAwayListener onClickAway={() => setAnchor(null)}>
      <Flex>
        <StyledItemPicker
          minWidth={minWidth}
          disabled={disabled}
          data-cy={`${dataAttrName}-picker`}
          onClick={({ currentTarget }) =>
            !disabled &&
            setAnchor(anchor === currentTarget ? null : currentTarget)
          }
        >
          <Flex
            direction='row'
            width='100%'
            items='center'
            justify='space-between'
            data-custom-attr={`${dataAttrName}-picker`}
          >
            {children}
            <Icon
              color={disabled ? 'silverSand' : 'waikawaGray'}
              name={Boolean(anchor) ? 'ExpandLess' : 'ExpandMore'}
              size='large'
            />
          </Flex>
        </StyledItemPicker>
        <StyledMenu
          {...(isOnTopBar ? { pageyoffset: window.pageYOffset + 'px' } : {})}
          anchorEl={anchor}
          open={Boolean(anchor)}
          overlayItem={Boolean(OverlayItem)}
          onClose={() => setAnchor(null)}
          data-custom-attr={`${dataAttrName}s-container`}
          style={menuStyle}
        >
          {OverlayItem}
          {items.map((item, idx) => {
            const isSelectedIteration = isSelectedFunc(item);
            const key = item?.id ?? idx;
            const itemDisabled = item?.disabled ?? false;

            return (
              <MenuItem
                disabled={Boolean(OverlayItem)}
                component={MenuItem}
                onClick={() => !itemDisabled && handleItemChange(item)}
                style={getItemStyle(isSelectedIteration)}
                key={key}
                data-cy={`${dataAttrName}-picker-item`}
              >
                {itemMapper(item)}
              </MenuItem>
            );
          })}
        </StyledMenu>
      </Flex>
    </ClickAwayListener>
  );
};

export default ItemPicker;
